/**
 * The Coinbase CDP embedded-wallet session, bound to the app session (#940).
 *
 * The CDP SDK keeps its own session (a refresh token in browser storage) that
 * outlives page loads, and the app never ended it: after A signed out, or A
 * was replaced by B through any path (the Google OAuth doors included), A's
 * wallet stayed signed in. B opening the wallet saw A's address and balance,
 * and the wagmi connector would sign transfers from A's wallet (a tip, or a
 * withdrawal to an address B chose).
 *
 * Now every token change (null→A, A→B, A→null) ends the CDP session through
 * the auth store's session-change path. A CDP session present at that moment
 * can only belong to the account that just ended, or to an earlier app session,
 * so ending it never costs a live wallet anything but a silent re-authentication
 * with our JWT the next time the wallet is used. Boot restores the app session
 * without a token change, so a returning user keeps their wallet session.
 */
import { initialize, isSignedIn, signOut, type Config as CDPConfig } from '@coinbase/cdp-core';
import { WALLET_ENABLED } from '../config/features';
import { onSessionChange } from '../stores/session-read';
import { getCdpJwt } from './wallet.service';

// CDP SDK initialization state (singleton)
let cdpInitialized = false;
let cdpInitPromise: Promise<void> | null = null;

export function isCdpInitialized(): boolean {
  return cdpInitialized;
}

/**
 * Create CDP config with JWT authentication.
 * Centralized factory to avoid duplicate getJwt callback definitions.
 */
export function createCdpConfig(projectId: string, options?: Partial<CDPConfig>): CDPConfig {
  return {
    projectId,
    customAuth: {
      // This callback is called by the SDK when it needs a JWT
      getJwt: async () => {
        const result = await getCdpJwt();
        if (!result.success) {
          throw new Error(result.error || 'Failed to get CDP JWT');
        }
        return result.token;
      },
    },
    ...options,
  };
}

/**
 * Initialize the CDP SDK with JWT authentication. Idempotent: called when the
 * provider mounts for an authenticated user, and before ending a session.
 */
export async function initializeCdpSdk(projectId: string): Promise<void> {
  if (cdpInitialized) return;
  if (cdpInitPromise) return cdpInitPromise;

  cdpInitPromise = (async () => {
    const cdpConfig = createCdpConfig(projectId, {
      ethereum: {
        // Create EOA accounts on login (Smart Accounts can be added later if needed)
        createOnLogin: 'eoa',
      },
    });

    await initialize(cdpConfig);
    cdpInitialized = true;
    console.log('CDP SDK initialized with JWT authentication');
  })();

  return cdpInitPromise;
}

/**
 * Wallet access is FAIL-CLOSED (Codex r7 P1 on #940). Every app-session change
 * blocks it synchronously, before anything can read, publish or sign, and it
 * stays blocked until the CURRENT account completes a fresh CDP sign-in:
 *
 *   'open'       a fresh sign-in by the CURRENT session completed: the wallet
 *                may be used.
 *   'blocked'    the state at boot, and after every session change. Whatever
 *                CDP session the SDK holds (restored from storage at boot, or
 *                left by the previous account) is not trusted: the current
 *                account's own sign-in ends it first, then signs in afresh.
 *   'sealed'     signOut() kept failing, so the persisted session was cleared
 *                by hand, but the SDK may still hold the previous account in
 *                memory. Nothing may open the wallet in this page load: a
 *                fresh sign-in could resume that account. A reload starts
 *                clean from the cleared storage.
 */
export type WalletAccess = 'open' | 'blocked' | 'sealed';

// Locked at BOOT too (#940 r8): a CDP session restored from storage may
// predate this binding and belong to another account.
let access: WalletAccess = 'blocked';
const accessListeners = new Set<() => void>();

function setAccess(next: WalletAccess): void {
  if (access === next) return;
  access = next;
  accessListeners.forEach((listener) => listener());
}

export function getWalletAccess(): WalletAccess {
  return access;
}

export function subscribeWalletAccess(listener: () => void): () => void {
  accessListeners.add(listener);
  return () => accessListeners.delete(listener);
}

/**
 * Where the CDP SDK (cdp-core 0.0.x, web) persists its session in
 * localStorage: the refresh token, and the persisted provider store holding
 * the CDP user and its accounts. wagmi keeps its own connection record too.
 */
export const CDP_PERSISTED_KEYS = [
  'cdp_refresh_token',
  'cdp-provider-store',
  'wagmi.store',
  'wagmi.recentConnectorId',
] as const;

function hardClearPersistedSession(): void {
  for (const key of CDP_PERSISTED_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Clearing ${key} failed:`, error);
    }
  }
}

const SIGN_OUT_ATTEMPTS = 3;
const SIGN_OUT_BACKOFF_MS = 100;

/** The latest end-of-session teardown; readers wait for it before asking CDP. */
let ending: Promise<void> = Promise.resolve();

/**
 * End whatever CDP session is signed in: signOut() with bounded backoff, and
 * if it never succeeds, clear the persisted session by hand and SEAL the
 * wallet for this page load. Serialized, and never rejects.
 */
export function endCdpSession(): Promise<void> {
  setAccess(access === 'sealed' ? 'sealed' : 'blocked');
  ending = ending.then(async () => {
    const projectId = import.meta.env.VITE_COINBASE_PROJECT_ID;
    if (!projectId) return;
    for (let attempt = 0; attempt < SIGN_OUT_ATTEMPTS; attempt += 1) {
      try {
        // The SDK must be initialized to read (and clear) its persisted session.
        await initializeCdpSdk(projectId);
        if (await isSignedIn()) await signOut();
        return;
      } catch (error) {
        console.error('Ending the embedded-wallet session failed:', error);
        await new Promise((r) => setTimeout(r, SIGN_OUT_BACKOFF_MS * 2 ** attempt));
      }
    }
    hardClearPersistedSession();
    setAccess('sealed');
  });
  return ending;
}

/**
 * Make way for a fresh sign-in by the current account: unless the wallet is
 * already open for this session, end whatever session the SDK holds (with the
 * same retry and seal as a session change). Resolves to whether a sign-in may
 * proceed: false when sealed.
 */
export async function prepareFreshCdpSignIn(): Promise<boolean> {
  if (access === 'open') return true;
  await endCdpSession();
  return getWalletAccess() !== 'sealed';
}

/** Resolves once any teardown started by a session change has finished. */
export function cdpSessionSettled(): Promise<void> {
  return ending;
}

/**
 * A fresh CDP sign-in by the current session completed: the wallet opens.
 * Refused while sealed, and for a sign-in whose session has since ended.
 */
export function openWalletAccess(sessionStillCurrent: boolean): boolean {
  if (access === 'sealed' || !sessionStillCurrent) return false;
  setAccess('open');
  return true;
}

if (WALLET_ENABLED) {
  onSessionChange(() => {
    void endCdpSession();
  });
}
